"use client";

import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import { createPortal } from "react-dom";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Building2, MessageSquare, Trash2, Eye, EyeOff, Star, Bug } from "lucide-react";
import { hideConversation, addFavoriteContact, removeFavoriteContact } from "@/app/actions/messaging";
import { toast } from "sonner";
import { UserProfilePopover } from "./user-profile-popover";
import { OnlineIndicator } from "@/components/ui/online-indicator";

interface ConversationListItemProps {
  conversation: {
    id: string;
    lastMessageAt: Date | null;
    lastMessagePreview: string | null;
    otherUser: {
      id: string;
      name: string | null;
      email: string | null;
      image: string | null;
    };
    listing: {
      id: string;
      title: string;
      address: string;
      price: number;
      images: string[];
    } | null;
    bugReport: {
      id: string;
      category: string;
      priority: string;
      status: string;
      url: string;
      description: string;
    } | null;
    unreadCount: number;
  };
  isActive?: boolean;
  isFavorite?: boolean;
  onClick: () => void;
  onDelete?: () => void;
  onFavoriteChange?: () => void;
}

export function ConversationListItem({
  conversation,
  isActive = false,
  isFavorite = false,
  onClick,
  onDelete,
  onFavoriteChange,
}: ConversationListItemProps) {
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);
  const contextMenuRef = useRef<HTMLDivElement>(null);

  // Ensure we only render portal on client side
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Close context menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setShowContextMenu(false);
      }
    }

    if (showContextMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showContextMenu]);

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
    setShowContextMenu(true);
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowContextMenu(false);

    const confirmed = window.confirm(
      `Delete conversation with ${conversation.otherUser.name || "User"}? This will hide the conversation for you.`
    );
    if (!confirmed) return;

    setIsDeleting(true);

    try {
      const result = await hideConversation(conversation.id);

      if (result.error) {
        toast.error(result.error);
      } else {
        toast.success("Conversation deleted");
        onDelete?.();
      }
    } catch (error) {
      console.error("Failed to delete conversation:", error);
      toast.error("Failed to delete conversation");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleMarkAsRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowContextMenu(false);
    // TODO: Implement mark as read functionality
    toast.info("Mark as read feature coming soon");
  };

  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowContextMenu(false);
    setIsTogglingFavorite(true);

    try {
      const result = isFavorite
        ? await removeFavoriteContact(conversation.otherUser.id)
        : await addFavoriteContact(conversation.otherUser.id);

      if (result.error) {
        toast.error(result.error);
      } else {
        toast.success(isFavorite ? "Removed from favorites" : "Added to favorites");
        onFavoriteChange?.();
      }
    } catch (error) {
      console.error("Failed to toggle favorite:", error);
      toast.error("Failed to update favorite");
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  return (
    <>
    <div
      className={cn(
        "relative cursor-pointer rounded-lg",
        "transition-[background-color,border-color,box-shadow,transform] duration-200 ease-out",
        "hover:bg-card/8 hover:shadow-xl hover:shadow-cyan-500/20 hover:scale-[1.01]",
        "border border-transparent hover:border-cyan-500/30",
        isActive && [
          "bg-cyan-500/15 border-cyan-500/40",
          "shadow-[0_0_20px_rgba(6,182,212,0.25),inset_0_1px_0_rgba(6,182,212,0.1)]"
        ],
        "p-3.5 group",
        "will-change-transform",
        isDeleting && "opacity-50 pointer-events-none"
      )}
      onClick={onClick}
      onContextMenu={handleContextMenu}
    >
      <div className="flex items-start gap-3">
        {/* Avatar with enhanced border and glow - wrapped in UserProfilePopover */}
        <div className="relative">
          <UserProfilePopover
            user={conversation.otherUser}
            isFavorite={isFavorite}
            onFavoriteChange={onFavoriteChange}
            side="right"
            align="start"
          >
            <Avatar className={cn(
              "h-10 w-10 border-2 ring-2 transition-all duration-200",
              isActive
                ? "border-cyan-500/40 ring-cyan-500/20"
                : "border-border/15 ring-white/5 group-hover:border-cyan-500/30",
              "hover:ring-cyan-500/50"
            )}>
              <AvatarImage src={conversation.otherUser.image || undefined} />
              <AvatarFallback className={cn(
                "bg-gray-800 text-sm transition-colors duration-200",
                isActive ? "text-cyan-400" : "text-gray-400 group-hover:text-cyan-400"
              )}>
                {conversation.otherUser.name?.[0] || "U"}
              </AvatarFallback>
            </Avatar>
          </UserProfilePopover>
          {/* Favorite star indicator */}
          {isFavorite && (
            <div className="absolute -top-1 -right-1 h-5 w-5 bg-yellow-500 rounded-full border-2 border-gray-900 flex items-center justify-center shadow-lg shadow-yellow-500/50 pointer-events-none z-10">
              <Star className="h-3 w-3 text-gray-900 fill-gray-900" />
            </div>
          )}
          {/* Online status indicator */}
          <OnlineIndicator userId={conversation.otherUser.id} position="bottom-right" size="sm" className="shadow-lg shadow-green-500/50" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header Row */}
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="flex-1 min-w-0">
              <h3 className={cn(
                "font-semibold text-sm truncate transition-colors duration-200",
                isActive ? "text-cyan-300 drop-shadow-sm" : "text-gray-200 group-hover:text-cyan-300"
              )}>
                {conversation.otherUser.name || "User"}
              </h3>

              {/* Connection Context with icon glow */}
              {conversation.bugReport ? (
                <div className="flex items-center gap-1.5 mt-1">
                  <Bug className={cn(
                    "h-3 w-3 shrink-0 transition-all duration-200",
                    isActive
                      ? "text-orange-400 drop-shadow-[0_0_6px_rgba(249,115,22,0.6)]"
                      : "text-orange-500/70 group-hover:text-orange-400"
                  )} />
                  <Badge variant="outline" className={cn(
                    "text-[10px] px-1.5 py-0 h-4 transition-colors duration-200",
                    isActive
                      ? "bg-orange-500/20 text-orange-400 border-orange-500/40"
                      : "bg-orange-500/10 text-orange-600 border-orange-500/30 group-hover:bg-orange-500/20 group-hover:text-orange-400"
                  )}>
                    Support
                  </Badge>
                  <p className={cn(
                    "text-xs truncate transition-colors duration-200",
                    isActive ? "text-orange-400/90" : "text-orange-500/70 group-hover:text-orange-400"
                  )}>
                    {conversation.bugReport.category.replace(/_/g, " ")}
                  </p>
                </div>
              ) : conversation.listing ? (
                <div className="flex items-center gap-1.5 mt-1">
                  <Building2 className={cn(
                    "h-3 w-3 shrink-0 transition-all duration-200",
                    isActive
                      ? "text-cyan-400 drop-shadow-[0_0_6px_rgba(6,182,212,0.6)]"
                      : "text-cyan-500/70 group-hover:text-cyan-400"
                  )} />
                  <p className={cn(
                    "text-xs truncate transition-colors duration-200",
                    isActive ? "text-cyan-400/90" : "text-cyan-500/70 group-hover:text-cyan-400"
                  )}>
                    {conversation.listing.title}
                  </p>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 mt-1">
                  <MessageSquare className="h-3 w-3 text-gray-500 shrink-0 transition-colors duration-200 group-hover:text-gray-400" />
                  <p className="text-xs text-gray-500 transition-colors duration-200 group-hover:text-gray-400">Direct message</p>
                </div>
              )}
            </div>

            {/* Delete Button - appears on hover */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDelete}
              disabled={isDeleting}
              className={cn(
                "absolute top-2 right-2 h-7 w-7 z-[var(--z-modal)]",
                "opacity-0 group-hover:opacity-100 transition-opacity",
                "bg-destructive/10 hover:bg-destructive/20 text-destructive",
                "border border-destructive/20 hover:border-destructive/40"
              )}
            >
              <Trash2 className="h-4 w-4" />
            </Button>

            {/* Metadata Column */}
            <div className="flex flex-col items-end gap-1.5">
              {conversation.lastMessageAt && (
                <span className="text-[10px] text-gray-500 whitespace-nowrap">
                  {formatDistanceToNow(
                    new Date(conversation.lastMessageAt),
                    { addSuffix: true }
                  )}
                </span>
              )}
              {conversation.unreadCount > 0 && (
                <Badge
                  variant="default"
                  className={cn(
                    "h-5 min-w-[20px] px-1.5 text-xs font-bold",
                    "bg-cyan-500 hover:bg-cyan-500",
                    "border border-cyan-400/50",
                    "shadow-lg shadow-cyan-500/40",
                    "animate-pulse-subtle"
                  )}
                >
                  {conversation.unreadCount > 99 ? "99+" : conversation.unreadCount}
                </Badge>
              )}
            </div>
          </div>

          {/* Message Preview with better typography */}
          {conversation.lastMessagePreview && (
            <p className={cn(
              "text-xs truncate mt-1.5 transition-colors duration-200",
              conversation.unreadCount > 0
                ? "text-gray-300 font-medium"
                : "text-gray-500 group-hover:text-gray-400"
            )}>
              {conversation.lastMessagePreview}
            </p>
          )}
        </div>

        {/* Optional: Listing Thumbnail with enhanced hover */}
        {conversation.listing && conversation.listing.images[0] && (
          <div className={cn(
            "hidden sm:block shrink-0 rounded-md overflow-hidden",
            "border-2 ring-1 transition-all duration-200",
            isActive
              ? "border-cyan-500/40 ring-cyan-500/20"
              : "border-border/10 ring-white/5 group-hover:border-cyan-500/40 group-hover:ring-cyan-500/20"
          )}>
            <Image
              src={conversation.listing.images[0]}
              alt={conversation.listing.title}
              width={48}
              height={48}
              className="h-12 w-12 object-cover transition-transform duration-200 group-hover:scale-110"
            />
          </div>
        )}
      </div>

      {/* Active Indicator Bar with animated glow */}
      {isActive && (
        <div className={cn(
          "absolute left-0 top-1/2 -translate-y-1/2 w-1 h-10 rounded-r-full",
          "bg-gradient-to-b from-cyan-400 via-cyan-500 to-cyan-600",
          "shadow-[0_0_8px_rgba(6,182,212,0.8)]",
          "animate-pulse-glow"
        )} />
      )}
    </div>

    {/* Context Menu - rendered via portal to ensure it's on top */}
    {isMounted && showContextMenu && createPortal(
      <div
        ref={contextMenuRef}
        className={cn(
          "fixed z-[var(--z-top)]",
          "min-w-[180px] rounded-lg",
          "bg-gray-900/95 backdrop-blur-xl",
          "border border-cyan-500/30",
          "shadow-[0_8px_32px_rgba(0,0,0,0.5),0_0_0_1px_rgba(6,182,212,0.2)]",
          "animate-in fade-in-0 zoom-in-95 duration-100"
        )}
        style={{
          left: `${contextMenuPosition.x}px`,
          top: `${contextMenuPosition.y}px`,
        }}
      >
        <div className="p-1">
          {/* Delete Conversation */}
          <button
            onClick={handleDelete}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md",
              "text-destructive hover:bg-destructive/10",
              "transition-colors duration-150",
              "cursor-pointer"
            )}
          >
            <Trash2 className="h-4 w-4" />
            <span>Delete Conversation</span>
          </button>

          {/* Divider */}
          <div className="h-px bg-card/10 my-1" />

          {/* Add/Remove from Favorites */}
          <button
            onClick={handleToggleFavorite}
            disabled={isTogglingFavorite}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md",
              "text-gray-300 hover:bg-card/5 hover:text-white",
              "transition-colors duration-150",
              "cursor-pointer",
              isTogglingFavorite && "opacity-50 cursor-not-allowed"
            )}
          >
            <Star className={cn("h-4 w-4", isFavorite && "fill-yellow-500 text-yellow-500")} />
            <span>{isFavorite ? "Remove from Favorites" : "Add to Favorites"}</span>
          </button>

          {/* Divider */}
          <div className="h-px bg-card/10 my-1" />

          {/* Mark as Read/Unread */}
          <button
            onClick={handleMarkAsRead}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md",
              "text-gray-300 hover:bg-card/5 hover:text-white",
              "transition-colors duration-150",
              "cursor-pointer"
            )}
          >
            {conversation.unreadCount > 0 ? (
              <>
                <Eye className="h-4 w-4" />
                <span>Mark as Read</span>
              </>
            ) : (
              <>
                <EyeOff className="h-4 w-4" />
                <span>Mark as Unread</span>
              </>
            )}
          </button>
        </div>
      </div>,
      document.body
    )}
    </>
  );
}